import { useMemo } from "react";
import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const dateOnly = (v: unknown): string =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) if (typeof v === "string" && v.trim() !== "") return v;
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};

// ── Encounters ←  Encounter ─────────────────────────────────────────────────

export type EncounterType =
  | "office_visit"
  | "telehealth"
  | "emergency"
  | "inpatient"
  | "outpatient"
  | "home_visit"
  | "lab_only";

export interface EncounterRecord {
  id: string;
  type: EncounterType;
  date: string; // "" when unknown
  time: string; // "" when unknown
  provider: string; // "" when unknown
  providerSpecialty: string; // "" when unknown
  facility: string; // "" when unknown
  reasonForVisit: string;
  chiefComplaint: string; // "" when unknown
  diagnosis: string[];
  procedures: string[];
  followUp: string; // "" when unknown
  notes: string; // "" when unknown
}

function mapEncounterType(cls: string, display: string, type: string): EncounterType {
  const hay = `${cls} ${display} ${type}`.toLowerCase();
  if (hay.includes("tele") || hay.includes("virtual") || hay.includes("video")) return "telehealth";
  if (hay.includes("emergency") || hay.includes("er ") || hay.includes("ed ")) return "emergency";
  if (hay.includes("inpatient") || hay.includes("admission") || hay.includes("ip")) return "inpatient";
  if (hay.includes("home")) return "home_visit";
  if (hay.includes("lab")) return "lab_only";
  if (hay.includes("outpatient") || hay.includes("op")) return "outpatient";
  return "office_visit";
}

// ── Clinical notes ←  DocumentReference ─────────────────────────────────────

export type ClinicalNoteType =
  | "progress_note"
  | "consultation"
  | "discharge_summary"
  | "procedure_note"
  | "nursing_note"
  | "referral";

export interface ClinicalNoteRecord {
  id: string;
  date: string; // "" when unknown
  type: ClinicalNoteType;
  provider: string; // "" when unknown
  providerCredentials: string; // "" when unknown
  specialty: string; // "" when unknown
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  /** "" — the note body lives in a stored binary, not returned with the record. */
  content: string;
  hasExtractedContent: boolean;
  diagnoses: string[];
  signedDate: string; // "" when unknown
}

function mapNoteType(docType: string, category: string): ClinicalNoteType {
  const hay = `${docType} ${category}`.toLowerCase();
  if (hay.includes("discharge")) return "discharge_summary";
  if (hay.includes("consult")) return "consultation";
  if (hay.includes("procedure") || hay.includes("operative") || hay.includes("op note")) return "procedure_note";
  if (hay.includes("nursing")) return "nursing_note";
  if (hay.includes("referral")) return "referral";
  return "progress_note";
}

// ── Treatment plans ←  CarePlan ────────────────────────────────────────────

export interface TreatmentGoalRecord {
  id: string;
  description: string;
  targetDate: string; // "" when unknown
  status: "not_started" | "in_progress" | "achieved" | "modified";
  /** null — CarePlan does not carry a numeric progress value. */
  progress: number | null;
}

export interface TreatmentPlanRecord {
  id: string;
  diagnosisId: string;
  diagnosisName: string;
  createdDate: string; // "" when unknown
  createdBy: string; // "" when unknown
  status: "active" | "completed" | "discontinued";
  goals: TreatmentGoalRecord[];
  /** [] — CarePlan does not enumerate medications; see the Medications page. */
  medications: never[];
  /** [] — CarePlan does not enumerate scheduled appointments. */
  appointments: never[];
  lifestyle: string[];
  notes: string; // "" when unknown
  nextReviewDate: string; // "" when unknown
  /** True when at least one goal has a real numeric progress value (never, today). */
  hasProgressData: boolean;
}

function mapPlanStatus(status: string): TreatmentPlanRecord["status"] {
  const s = status.toLowerCase();
  if (s.includes("complet")) return "completed";
  if (s.includes("revoked") || s.includes("cancel") || s.includes("discontinu") || s.includes("ended")) {
    return "discontinued";
  }
  return "active";
}

function mapGoalStatus(activityStatus: string): TreatmentGoalRecord["status"] {
  const s = activityStatus.toLowerCase();
  if (s.includes("complet") || s.includes("achiev")) return "achieved";
  if (s.includes("progress") || s.includes("in-progress") || s.includes("active")) return "in_progress";
  if (s.includes("cancel") || s.includes("stopped")) return "modified";
  return "not_started";
}

// ── Care providers ←  Practitioner ─────────────────────────────────────────

export interface CareProviderRecord {
  id: string;
  name: string;
  credentials: string; // "" when unknown
  specialty: string; // "" when unknown
  subSpecialty: string; // "" when unknown
  role: "primary_care" | "specialist" | "surgeon" | "therapist" | "dentist" | "optometrist" | "other";
  facility: string; // "" when unknown
  address: string; // "" when unknown
  phone: string; // "" when unknown
  email: string; // "" when unknown
  npi: string; // "" when unknown
  lastSeenDate: string; // "" when unknown
  nextAppointment: string; // "" when unknown
  notes: string; // "" when unknown
}

function mapProviderRole(qualification: string): CareProviderRecord["role"] {
  const s = qualification.toLowerCase();
  if (s.includes("surg")) return "surgeon";
  if (s.includes("therap") || s.includes("physical therapy") || s.includes("psych")) return "therapist";
  if (s.includes("dent")) return "dentist";
  if (s.includes("optom") || s.includes("ophthalm")) return "optometrist";
  if (s.includes("family") || s.includes("primary") || s.includes("general practice") || s.includes("internal medicine")) {
    return "primary_care";
  }
  if (s) return "specialist";
  return "other";
}

export interface CareTreatmentResult {
  encounters: EncounterRecord[];
  notes: ClinicalNoteRecord[];
  plans: TreatmentPlanRecord[];
  providers: CareProviderRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCareTreatment(): CareTreatmentResult {
  const { records, isLoading, error, refresh } = useParsedRecords();

  return useMemo(() => {
    // Cross-reference conditions/procedures back to their encounter.
    const dxByEncounter = new Map<string, string[]>();
    const procByEncounter = new Map<string, string[]>();
    for (const r of records) {
      const ref = str(r.fields.encounter_ref);
      if (!ref) continue;
      if (r.resourceType === "Condition") {
        const name = firstStr(r.fields.display_name);
        if (name) dxByEncounter.set(ref, [...(dxByEncounter.get(ref) ?? []), name]);
      }
      if (r.resourceType === "Procedure") {
        const name = firstStr(r.fields.display_name);
        if (name) procByEncounter.set(ref, [...(procByEncounter.get(ref) ?? []), name]);
      }
    }

    const encounters: EncounterRecord[] = records
      .filter((r) => r.resourceType === "Encounter")
      .map((r) => {
        const f = r.fields;
        return {
          id: r.id,
          type: mapEncounterType(str(f.encounter_class), str(f.encounter_class_display), str(f.encounter_type)),
          date: firstDate(f.start_date, f.end_date),
          time: "",
          provider: firstStr(f.practitioner_name, f.practitioner_display, f.service_provider),
          providerSpecialty: firstStr(f.practitioner_qualification),
          facility: firstStr(f.location_display, f.service_provider),
          reasonForVisit: firstStr(f.reason_display, f.encounter_type, f.encounter_class_display) || "Visit",
          chiefComplaint: "",
          diagnosis: dxByEncounter.get(`Encounter/${r.id}`) ?? dxByEncounter.get(r.id) ?? [],
          procedures: procByEncounter.get(`Encounter/${r.id}`) ?? procByEncounter.get(r.id) ?? [],
          followUp: "",
          notes: "",
        };
      })
      .sort((a, b) => b.date.localeCompare(a.date));

    const notes: ClinicalNoteRecord[] = records
      .filter((r) => r.resourceType === "DocumentReference")
      .map((r) => {
        const f = r.fields;
        const date = firstDate(f.authored_date);
        return {
          id: r.id,
          date,
          type: mapNoteType(str(f.document_type), str(f.category)),
          provider: firstStr(f.author_display),
          providerCredentials: "",
          specialty: firstStr(f.practice_setting),
          subjective: "",
          objective: "",
          assessment: "",
          plan: "",
          content: "",
          hasExtractedContent: f.has_extracted_content === true,
          diagnoses: [],
          signedDate: date,
        };
      })
      .sort((a, b) => b.date.localeCompare(a.date));

    const plans: TreatmentPlanRecord[] = records
      .filter((r) => r.resourceType === "CarePlan")
      .map((r) => {
        const f = r.fields;
        const goalText = firstStr(f.goal_display);
        const goals: TreatmentGoalRecord[] = goalText
          ? [
              {
                id: `${r.id}-goal`,
                description: goalText,
                targetDate: "",
                status: mapGoalStatus(str(f.activity_status)),
                progress: null,
              },
            ]
          : [];
        const narrative = firstStr(f.narrative_text, f.activity_code_text);
        return {
          id: r.id,
          diagnosisId: "",
          diagnosisName: firstStr(f.addresses_display, f.category, f.activity_code_text) || "Care plan",
          createdDate: "",
          createdBy: "",
          status: mapPlanStatus(str(f.status)),
          goals,
          medications: [],
          appointments: [],
          lifestyle: narrative ? [narrative] : [],
          notes: narrative,
          nextReviewDate: "",
          hasProgressData: false,
        };
      });

    const providers: CareProviderRecord[] = records
      .filter((r) => r.resourceType === "Practitioner")
      .map((r) => {
        const f = r.fields;
        const name =
          firstStr(f.full_name) ||
          [str(f.given_name), str(f.family_name)].filter(Boolean).join(" ") ||
          "Unknown provider";
        const qualification = firstStr(f.qualification);
        return {
          id: r.id,
          name,
          credentials: qualification,
          specialty: qualification,
          subSpecialty: "",
          role: mapProviderRole(qualification),
          facility: "",
          address: "",
          phone: "",
          email: firstStr(f.email),
          npi: firstStr(f.npi),
          lastSeenDate: "",
          nextAppointment: "",
          notes: "",
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    return { encounters, notes, plans, providers, isLoading, error, refresh };
  }, [records, isLoading, error, refresh]);
}
